/*
 * Note level selector
 * Features: Single-select level chips matching the existing team picker and audience help.
 * Implementation: Native radio inputs retain keyboard behavior with visible focus on each chip.
 * Recent changes: Adds a popup-only segmented presentation while preserving wrap and vertical chips.
 */

import { useId } from 'react';
import { NOTE_ACCESS_LEVELS, type AccessLevel } from '../../constants/noteVisibilityConstants';
import { THEME_CLASSES } from '../../constants/themeConstants';

interface NoteLevelSelectorProps {
  value?: number | null;
  onChange: (level: AccessLevel) => void;
  isEditable: boolean;
  layout?: 'wrap' | 'vertical' | 'segmented';
}

export default function NoteLevelSelector({ value, onChange, isEditable, layout = 'wrap' }: NoteLevelSelectorProps) {
  const groupId = useId();
  const selectedOption = NOTE_ACCESS_LEVELS.find(option => option.value === value);
  const isSegmented = layout === 'segmented';

  return (
    <div id="note-level-selector">
      <div
        id="note-level-options"
        role="radiogroup"
        aria-label="Who can view"
        aria-describedby={`${groupId}-help`}
        className={isSegmented
          ? 'grid w-full grid-cols-4 gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-900'
          : `flex w-full gap-2 px-1 py-0.5 ${layout === 'vertical' ? 'flex-col items-start' : 'flex-wrap'}`
        }
      >
        {NOTE_ACCESS_LEVELS.map(option => (
          <label
            key={option.value}
            className={`relative inline-flex max-w-full items-center justify-center text-xs font-medium transition-colors focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 ${isSegmented ? 'min-w-0 rounded-lg px-2 py-2' : 'w-fit rounded-full px-3 py-1'} ${isEditable ? 'cursor-pointer' : 'cursor-not-allowed'} ${value === option.value
              ? (isSegmented ? `bg-white shadow-sm ${THEME_CLASSES.text.primary} dark:bg-slate-800` : THEME_CLASSES.button.primary)
              : (isSegmented ? `${THEME_CLASSES.text.secondary} ${isEditable ? 'hover:bg-white/70 dark:hover:bg-slate-800/70' : ''}` : `${THEME_CLASSES.text.secondary} ${THEME_CLASSES.border} border ${isEditable ? 'hover:bg-opacity-10 hover:bg-primary' : ''}`)}`}
          >
            <input
              type="radio"
              name={groupId}
              value={option.value}
              checked={value === option.value}
              disabled={!isEditable}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            {option.label}
          </label>
        ))}
      </div>
      <p id={`${groupId}-help`} className={`mt-3 text-xs ${THEME_CLASSES.text.secondary}`} aria-live="polite">
        {selectedOption?.helpText ?? 'Select a level to set who can view this note.'}
      </p>
    </div>
  );
}
